import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Risk } from "./risk";

export interface ChangeField {
  label: string;
  value: string;
}
/** A single value, or a list of labelled fields (paired by label). */
export type ChangeValue = string | readonly ChangeField[];

interface Props {
  open: boolean;
  title: string;
  body?: ReactNode;
  before?: ChangeValue;
  after?: ChangeValue;
  risk?: Risk;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const OVERLAY: CSSProperties = {
  position: "fixed", inset: 0, zIndex: 50,
  display: "flex", alignItems: "center", justifyContent: "center",
  background: "rgba(0,0,0,0.6)",
};
const SHEET: CSSProperties = {
  background: "var(--d-ink-2)", borderRadius: 22, padding: 24,
  maxWidth: 420, width: "calc(100% - 48px)",
  display: "flex", flexDirection: "column", gap: 16,
  border: "1px solid var(--d-line)",
};
const TITLE: CSSProperties = {
  fontFamily: "var(--font-display)", fontSize: 20, color: "var(--d-fg)",
};
const BODY: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 14, color: "var(--d-fg-muted)",
};
const DIFF: CSSProperties = {
  display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 8,
  fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--d-fg)",
  overflowWrap: "anywhere",
};
const MUTED: CSSProperties = { color: "var(--d-fg-muted)" };
const ROW: CSSProperties = { display: "flex", justifyContent: "flex-end", gap: 8 };
const BTN_BASE: CSSProperties = {
  padding: "8px 16px", borderRadius: 999, fontFamily: "var(--font-body)",
  border: "1px solid var(--d-line)", background: "transparent",
  color: "var(--d-fg)", cursor: "pointer",
};

function Diff({ before, after }: { before?: ChangeValue; after?: ChangeValue }): JSX.Element | null {
  const { t } = useTranslation("admin", { lng: "en" });
  if (before === undefined || after === undefined) return null;
  if (typeof before === "string" || typeof after === "string") {
    return (
      <div style={DIFF}>
        <span style={MUTED}>{t("shell.confirm.before")}</span>
        <span data-testid="confirm-before">{String(before)}</span>
        <span aria-hidden>→</span>
        <span style={MUTED}>{t("shell.confirm.after")}</span>
        <span data-testid="confirm-after">{String(after)}</span>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {after.map((f) => {
        const prev = before.find((b) => b.label === f.label)?.value ?? "—";
        return (
          <div key={f.label} style={DIFF} data-testid={`confirm-field-${f.label}`}>
            <span style={MUTED}>{f.label}</span>
            <span>{prev}</span>
            <span aria-hidden>→</span>
            <span>{f.value}</span>
          </div>
        );
      })}
    </div>
  );
}

export function ConfirmModal({
  open, title, body, before, after, risk, confirmLabel, cancelLabel, destructive,
  onConfirm, onClose,
}: Props): JSX.Element | null {
  const { t } = useTranslation("admin");
  const { t: tEn } = useTranslation("admin", { lng: "en" });
  if (!open) return null;
  const isDanger = destructive || risk === "danger";
  const hasDiff = before !== undefined && after !== undefined;
  const confirmStyle: CSSProperties = {
    ...BTN_BASE,
    background: isDanger ? "var(--d-ember)" : "var(--d-violet)",
    color: "#fff", borderColor: "transparent",
  };
  return (
    <div role="dialog" aria-modal="true" style={OVERLAY} onClick={onClose}>
      <div style={SHEET} onClick={(e) => e.stopPropagation()}>
        <div style={TITLE}>{title}</div>
        {body ? <div style={BODY}>{body}</div> : null}
        <Diff before={before} after={after} />
        <div style={ROW}>
          <button style={BTN_BASE} onClick={onClose}>
            {cancelLabel ?? (hasDiff ? tEn("shell.confirm.cancel") : t("common.cancel"))}
          </button>
          <button style={confirmStyle} onClick={onConfirm}>
            {confirmLabel ?? (hasDiff ? tEn("shell.confirm.apply") : t("common.confirm"))}
          </button>
        </div>
      </div>
    </div>
  );
}
